// ---------------------------------------------------------------------------
// Water Sensor quantity-break pricing — helpers only, NO data.
// ---------------------------------------------------------------------------
// The tier data is the single source of truth: the `custom.water_sensor_tiers`
// metafield on the Water Sensor product, read via the Storefront API and passed
// into these helpers. Nothing is hardcoded here, so the site can never drift
// from what the Shopify Function charges (it reads the same metafield).
//
// A tiers map is { "<quantity>": <total price>, ... }.

// The tiers checkout actually charges for this product, or null: only while the
// volume discount is live in Shopify (`volumePricing`). The limited-time offer's
// Function follows the same switch (the server keeps its volume flag in step), so
// with volume pricing off an offer takes its percent off the list price instead.
export const tiersInEffect = (product, { volumePricing }) => {
    return (volumePricing && product && product.tiers) ? product.tiers : null;
};

// Normalize a metafield value (JSON string or already-parsed object) -> map|null.
export const parseTiers = (raw) => {
    if(!raw) {
        return null;
    }
    if(typeof raw === 'object') {
        return raw;
    }
    try {
        return JSON.parse(raw);
    } catch(_e) {
        return null;
    }
};

const qtyKeys = (tiers) => Object.keys(tiers || {}).map(Number).filter((k) => !Number.isNaN(k));

// Total price for `qty` sensors. Direct lookup within the table; above the top
// tier, extrapolate at the top tier's per-unit rate. Returns 0 with no tiers.
export const waterSensorTotal = (tiers, qty) => {
    const n = parseInt(qty, 10) || 0;
    if(!tiers || n <= 0) {
        return 0;
    }
    if(tiers[n] != null) {
        return Number(tiers[n]);
    }
    const keys = qtyKeys(tiers);
    if(!keys.length) {
        return 0;
    }
    const max = Math.max(...keys);
    if(n > max) {
        return Math.round((Number(tiers[max]) / max) * n * 100) / 100;
    }
    const min = Math.min(...keys);
    return Math.round((Number(tiers[min]) / min) * n * 100) / 100;
};

// Effective per-sensor price at a given quantity (for "as low as $X each" copy).
export const waterSensorPerUnit = (tiers, qty) => {
    const n = parseInt(qty, 10) || 0;
    return n > 0 ? Math.round((waterSensorTotal(tiers, n) / n) * 100) / 100 : 0;
};

// Savings vs. buying the same count at the single-unit price.
export const waterSensorSavings = (tiers, qty) => {
    const n = parseInt(qty, 10) || 0;
    if(!tiers || n <= 1) {
        return 0;
    }
    const unit = tiers[1] != null ? Number(tiers[1]) : waterSensorPerUnit(tiers, 1);
    return Math.max(0, Math.round((unit * n - waterSensorTotal(tiers, n)) * 100) / 100);
};

// Highest quantity defined in the table (for "as low as $X/ea at N" copy).
export const maxTierQty = (tiers) => {
    const keys = qtyKeys(tiers);
    return keys.length ? Math.max(...keys) : 0;
};
